import { useState, useCallback, useMemo, useRef } from 'react';
import { Modal as RNModal, View, StyleSheet, useWindowDimensions, Pressable } from 'react-native';
import FlashMessage from 'react-native-flash-message';
import { Button, Text, theme, styles as baseStyles, Icon } from '@socrait/react';

import { FlashBanner } from '~/components/FlashBanner';
import { Table, ColumnConfig } from '~/components/Table';
import { SMALL_SCREEN_WIDTH } from '~/constants';
import { ClientError } from '~/errors';
import { LayoutWithSidebar } from '~/layouts/LayoutWithSidebar';
import { usePeriodListAllQuery, usePeriodRestoreMutation } from '~/services/period';
import { Period } from '~/services/types';
import { ArchivedClassReviewScreen, ArchivedClassRosterScreen } from '~/screens/archivedClass';
import { getConciseDateTime } from '~/utils/date';
import { showError } from '~/utils/showError';
import { Loading } from '~/components/Loading';

type ArchivedClassesListScreenProps = {
  onClose: (flashMessage?: string, type?: string, shortName?: string) => void;
};

export function ArchivedClassesListScreen({ onClose }: ArchivedClassesListScreenProps) {
  const { data, status } = usePeriodListAllQuery({ includeArchived: true, includeStudents: true });
  const { mutate: restorePeriod, status: restorePeriodStatus } = usePeriodRestoreMutation();
  const notificationRef = useRef<FlashMessage>(null);

  const archivedPeriods = data?.periods.filter((period) => period.archivedAt !== null) || [];

  const { width: windowWidth } = useWindowDimensions();
  const isSmallScreen = windowWidth < SMALL_SCREEN_WIDTH;

  const isLoading = status === 'pending';

  const [activePeriod, setActivePeriod] = useState<Period | null>(null);
  const [restorePeriodId, setRestorePeriodId] = useState<string | null>(null);
  const [isArchivedReviewScreenOpen, setIsArchivedReviewScreenOpen] = useState(false);
  const [isArchivedRosterScreenOpen, setIsArchivedRosterScreenOpen] = useState(false);

  const handleActionButtonPress = useCallback(
    (period: Period, operation: 'review' | 'roster') => {
      setActivePeriod(period);
      if (operation === 'review') {
        setIsArchivedReviewScreenOpen(true);
      } else {
        setIsArchivedRosterScreenOpen(true);
      }
    },
    [setActivePeriod],
  );

  const handleError = useCallback((error: Error, ref?: React.RefObject<FlashMessage>) => {
    const errorStatusCode = error instanceof ClientError ? error.statusCode : null;
    const errorMessage =
      errorStatusCode === 409
        ? error.message
        : `Failed to restore class. Please try again, or contact support if the issue persists.`;

    showError(error, errorMessage, ref || notificationRef);
  }, []);

  const onRestore = useCallback(
    (period: Period, ref?: React.RefObject<FlashMessage>) => {
      setRestorePeriodId(period.id);
      restorePeriod(
        { periodId: period.id },
        {
          onSuccess: () => {
            setRestorePeriodId(null);
            setIsArchivedReviewScreenOpen(false);
            setIsArchivedRosterScreenOpen(false);
            onClose('Class successfully restored', 'success', period.shortName);
          },
          onError: (error) => {
            setRestorePeriodId(null);
            handleError(error, ref);
          },
        },
      );
    },
    [restorePeriod, handleError, onClose],
  );

  const columns = useMemo<ColumnConfig<Period>[]>(
    () => [
      {
        width: { type: 'fixed', width: 60 },
        align: 'center',
        header: '#',
        cellStyle: { paddingBottom: 0 },
        headerTextStyle: [styles.smallText, styles.headerText],
        render: (period) => (
          <View style={styles.shortName}>
            <Text style={styles.smallText}>{period.shortName}</Text>
          </View>
        ),
      },
      {
        width: { type: 'flex', flex: 1 },
        align: 'left',
        header: 'Class Name',
        headerTextStyle: styles.headerText,
        cellStyle: { paddingTop: theme.spacing.medium },
        render: (period, _index, isHovered) => (
          <View style={styles.classNameContainer}>
            <Pressable onPress={() => handleActionButtonPress(period, 'review')}>
              <Text style={isHovered && styles.classNameTextHovered}>{period.name}</Text>
              <Text>
                <Text style={[styles.smallText, styles.updatedAtText, baseStyles.boldText]}>Last updated: </Text>
                <Text style={[styles.smallText, styles.updatedAtText]}>
                  {getConciseDateTime(new Date(period.updatedAt))}
                </Text>
              </Text>
            </Pressable>
          </View>
        ),
      },
      {
        width: { type: 'fixed', width: 130 },
        align: 'center',
        cellStyle: { paddingBottom: 0 },
        renderHeader: () => (
          <View style={styles.archiveHeader}>
            <Icon type="archive" fill={theme.colors.navy} />
            <Text style={[styles.headerText, styles.smallText]}>Date Archived</Text>
          </View>
        ),
        render: (period) => (
          <Text style={styles.smallText}>{getConciseDateTime(new Date(period.archivedAt || ''))}</Text>
        ),
      },
      {
        width: { type: 'fixed', width: 215 },
        align: 'center',
        noBorder: true,
        header: '',
        render: (period) => (
          <View style={[styles.buttonContainer]}>
            <Button
              icon="eye"
              containerStyle={styles.iconButton}
              onPress={() => handleActionButtonPress(period, 'review')}
            />
            <Button
              icon="group"
              containerStyle={styles.iconButton}
              onPress={() => handleActionButtonPress(period, 'roster')}
            />
            <Button
              name="Restore"
              onPress={() => onRestore(period)}
              loading={restorePeriodId === period.id && restorePeriodStatus === 'pending'}
            />
          </View>
        ),
      },
    ],
    [restorePeriodId, restorePeriodStatus, handleActionButtonPress, onRestore],
  );

  const noArchivedClassesContent = useMemo(() => {
    return (
      <View style={styles.noArchivedClassesContainer}>
        <Icon type="archive" fill={theme.colors.darkGray} />
        <Text style={styles.noArchivedClassesText}>No archived classes found</Text>
      </View>
    );
  }, []);

  return (
    <>
      <LayoutWithSidebar isSimple={true}>
        <View style={[styles.container, isSmallScreen && styles.containerSmall]}>
          <View style={styles.header}>
            <Text style={styles.headerText}>Archived Classes</Text>
            <Pressable onPress={() => onClose()}>
              <Icon type="close" />
            </Pressable>
          </View>
          <View style={styles.tableContainer}>
            {isLoading ? (
              <Loading backgroundColor={theme.colors.white} />
            ) : archivedPeriods.length > 0 ? (
              <Table
                data={archivedPeriods}
                columns={columns}
                isLoading={isLoading}
                columnGap={20}
                accessibilityLabel="archived_classes_list_table"
                keyExtractor={(period) => period.id}
                minWidth={650}
                maxWidth={1500}
              />
            ) : (
              noArchivedClassesContent
            )}
          </View>
        </View>
        <RNModal
          visible={isArchivedReviewScreenOpen}
          onRequestClose={() => setIsArchivedReviewScreenOpen(false)}
          animationType="none">
          <ArchivedClassReviewScreen
            activePeriod={activePeriod}
            onClose={() => setIsArchivedReviewScreenOpen(false)}
            onRestore={onRestore}
            isRestoring={restorePeriodId === activePeriod?.id && restorePeriodStatus === 'pending'}
          />
        </RNModal>
        <RNModal
          visible={isArchivedRosterScreenOpen}
          onRequestClose={() => setIsArchivedRosterScreenOpen(false)}
          animationType="none">
          <ArchivedClassRosterScreen
            activePeriod={activePeriod}
            onClose={() => setIsArchivedRosterScreenOpen(false)}
            onRestore={onRestore}
            isRestoring={restorePeriodId === activePeriod?.id && restorePeriodStatus === 'pending'}
          />
        </RNModal>
      </LayoutWithSidebar>
      <FlashMessage
        ref={notificationRef}
        position="top"
        animationDuration={100}
        duration={10000}
        MessageComponent={({ message }: { message: { message: string; type: 'warning' | 'success' } }) => (
          <FlashBanner message={message} />
        )}
      />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  containerSmall: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    paddingRight: 0,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.xlarge * 2,
    paddingVertical: theme.spacing.xlarge * 1.5,
  },
  headerText: {
    ...baseStyles.boldText,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  tableContainer: {
    flex: 1,
    paddingHorizontal: theme.spacing.xxlarge,
    paddingBottom: theme.spacing.xxlarge,
  },
  archiveHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.medium,
  },
  classNameContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xsmall,
    width: '100%',
  },
  classNameTextHovered: {
    ...baseStyles.boldText,
    textDecorationLine: 'underline',
  },
  shortName: {
    backgroundColor: theme.colors.gray,
    borderRadius: 50,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: 50,
    width: 50,
  },
  smallText: {
    fontSize: theme.fontSizes.small,
  },
  updatedAtText: {
    color: theme.colors.darkGray,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.medium,
    opacity: 1,
  },
  iconButton: {
    padding: 0,
    width: 50,
  },
  noArchivedClassesContainer: {
    alignItems: 'center',
    backgroundColor: theme.colors.gray,
    borderRadius: theme.borderRadius.large,
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    padding: theme.spacing.medium,
    gap: theme.spacing.medium,
  },
  noArchivedClassesText: {
    color: theme.colors.darkGray,
    textAlign: 'center',
    fontSize: theme.fontSizes.small,
  },
});
